import type { Column } from '@tanstack/react-table'

export type DataTablePhoneRole =
  | 'primary'
  | 'secondary'
  | 'trailing'
  | 'actions'
  | 'meta'
  | 'hidden'

type PhoneColumn<TData> = Column<TData, unknown>

export function getPhoneRoles<TData>(columns: PhoneColumn<TData>[]) {
  const roles = new Map<string, DataTablePhoneRole>()

  for (const column of columns) {
    const meta = column.columnDef.meta
    if (column.id === 'select') roles.set(column.id, 'meta')
    else if (meta?.phone) roles.set(column.id, meta.phone)
    else if (meta?.actions) roles.set(column.id, 'trailing')
  }

  const dataColumns = columns.filter((column) => column.id !== 'select')
  const unassigned = () => dataColumns.filter((column) => !roles.has(column.id))
  const hasRole = (role: DataTablePhoneRole) =>
    dataColumns.some((column) => !column.columnDef.meta?.actions && roles.get(column.id) === role)

  if (!hasRole('primary')) {
    const first = unassigned()[0]
    if (first) roles.set(first.id, 'primary')
  }

  if (!hasRole('trailing')) {
    const numeric = unassigned().find((column) => column.columnDef.meta?.numeric)
    if (numeric) roles.set(numeric.id, 'trailing')
  }

  let metaCount = dataColumns.filter(
    (column) => !column.columnDef.meta?.actions && roles.get(column.id) === 'meta',
  ).length
  for (const column of unassigned()) {
    if (metaCount === 2) break
    roles.set(column.id, 'meta')
    metaCount += 1
  }

  for (const column of unassigned()) roles.set(column.id, 'hidden')

  return roles
}

export function getPhoneDetailIndexes<TData>(
  columns: PhoneColumn<TData>[],
  roles: Map<string, DataTablePhoneRole>,
) {
  const indexes = new Map<string, number>()

  for (const column of columns) {
    const role = roles.get(column.id)
    if (column.id === 'select' || (role !== 'secondary' && role !== 'meta')) continue
    indexes.set(column.id, indexes.size)
  }

  return indexes
}

export function getPhoneActionSlot<TData>(
  columns: PhoneColumn<TData>[],
  roles: Map<string, DataTablePhoneRole> | undefined,
) {
  const actionColumn = columns.find(
    (column) => column.columnDef.meta?.actions && roles?.get(column.id) === 'trailing',
  )
  if (actionColumn) return actionColumn.id

  const trailingColumns = columns.filter(
    (column) => column.id !== 'select' && roles?.get(column.id) === 'trailing',
  )
  return trailingColumns.length > 1 ? trailingColumns.at(-1)?.id : undefined
}

export function hasPhoneActions<TData>(
  columns: PhoneColumn<TData>[],
  roles: Map<string, DataTablePhoneRole> | undefined,
) {
  return getPhoneActionSlot(columns, roles) !== undefined
}

export const PHONE_TABLE_CLASSES = {
  table: '@max-[40rem]:block @max-[40rem]:min-w-0',
  header: '@max-[40rem]:sr-only',
  body: '@max-[40rem]:block @max-[40rem]:w-full',
  row: '@max-[40rem]:grid @max-[40rem]:items-center @max-[40rem]:gap-x-2 @max-[40rem]:gap-y-1 @max-[40rem]:h-auto @max-[40rem]:w-full @max-[40rem]:border-b @max-[40rem]:border-border-subtle @max-[40rem]:px-4 @max-[40rem]:py-3',
  detailLine:
    'hidden @max-[40rem]:block @max-[40rem]:col-start-1 @max-[40rem]:col-end-4 @max-[40rem]:row-start-2 @max-[40rem]:w-auto @max-[40rem]:overflow-visible @max-[40rem]:whitespace-normal @max-[40rem]:px-0 @max-[40rem]:py-0 @max-[40rem]:text-sm @max-[40rem]:text-muted-foreground',
  detailLineAfterSelection: '@max-[40rem]:col-start-2',
  detailLineAfterSelectionWithActions: '@max-[40rem]:col-end-5',
  detailLineAfterSelectionWithoutActions: '@max-[40rem]:col-end-4',
  detailLineWithoutSelectionOrActions: '@max-[40rem]:col-end-3',
} as const

const ROW_COLUMNS = [
  [
    '@max-[40rem]:grid-cols-[minmax(0,1fr)_max-content]',
    '@max-[40rem]:grid-cols-[minmax(0,1fr)_max-content_auto]',
  ],
  [
    '@max-[40rem]:grid-cols-[auto_minmax(0,1fr)_max-content]',
    '@max-[40rem]:grid-cols-[auto_minmax(0,1fr)_max-content_auto]',
  ],
] as const
const PRIMARY_COLUMNS = ['@max-[40rem]:col-start-1', '@max-[40rem]:col-start-2'] as const
const TRAILING_COLUMNS = ['@max-[40rem]:col-start-2', '@max-[40rem]:col-start-3'] as const
const ACTION_COLUMNS = ['@max-[40rem]:col-start-3', '@max-[40rem]:col-start-4'] as const

export function phoneRowClass(hasSelection: boolean, hasActions: boolean) {
  const columns = hasSelection ? ROW_COLUMNS[1] : ROW_COLUMNS[0]
  return `${PHONE_TABLE_CLASSES.row} ${columns[Number(hasActions)]}`
}

export function phoneCellClass(
  role: DataTablePhoneRole | undefined,
  options: {
    selection: boolean
    actions: boolean
    hasSelection: boolean
  },
) {
  const base =
    '@max-[40rem]:px-0 @max-[40rem]:py-0 @max-[40rem]:first:ps-0 @max-[40rem]:last:pe-0 @max-[40rem]:w-auto @max-[40rem]:min-w-0 @max-[40rem]:max-w-none'
  if (options.selection) {
    return `${base} @max-[40rem]:col-start-1 @max-[40rem]:row-start-1 @max-[40rem]:grid @max-[40rem]:size-10 @max-[40rem]:place-items-center`
  }
  if (role === 'primary') {
    const column = options.hasSelection ? PRIMARY_COLUMNS[1] : PRIMARY_COLUMNS[0]
    return `${base} ${column} @max-[40rem]:row-start-1 @max-[40rem]:w-full @max-[40rem]:whitespace-normal`
  }
  if (role === 'trailing') {
    const column = options.actions
      ? ACTION_COLUMNS[Number(options.hasSelection)]
      : TRAILING_COLUMNS[Number(options.hasSelection)]
    return `${base} ${column} @max-[40rem]:row-start-1 @max-[40rem]:overflow-visible @max-[40rem]:justify-self-end${options.actions ? '' : ' @max-[40rem]:w-max @max-[40rem]:min-w-max @max-[40rem]:whitespace-nowrap @max-[40rem]:tabular-nums'}`
  }
  if (role === 'actions') {
    return `${base} @max-[40rem]:col-start-1 @max-[40rem]:col-span-full @max-[40rem]:row-start-3 @max-[40rem]:w-full @max-[40rem]:pt-2`
  }
  return '@max-[40rem]:hidden'
}
